import * as React from "react";
import { cn } from "@/util/cn";

export type SegmentedNumericInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type" | "inputMode" | "maxLength"
> & {
  maxLength: number;
  slotClassName?: string;
};

const SegmentedNumericInput = React.forwardRef<
  HTMLInputElement,
  SegmentedNumericInputProps
>(
  (
    {
      maxLength,
      slotClassName,
      value,
      onChange,
      onFocus,
      onBlur,
      disabled,
      className,
      ...props
    },
    forwardedRef
  ) => {
    const localRef = React.useRef<HTMLInputElement>(null);

    React.useImperativeHandle(
      forwardedRef,
      () => localRef.current as HTMLInputElement
    );

    const [isFocused, setIsFocused] = React.useState(false);
    const [selectionStart, setSelectionStart] = React.useState<number | null>(
      null
    );
    const [selectionEnd, setSelectionEnd] = React.useState<number | null>(null);

    const rawValue = typeof value === "string" ? value : "";

    const visibleSlotCount = Math.min(maxLength, Math.max(10, rawValue.length));

    const syncSelection = React.useCallback(() => {
      setSelectionStart(localRef.current?.selectionStart ?? null);
      setSelectionEnd(localRef.current?.selectionEnd ?? null);
    }, []);

    React.useEffect(() => {
      if (isFocused) {
        const frame = requestAnimationFrame(syncSelection);
        return () => cancelAnimationFrame(frame);
      }
    }, [rawValue, isFocused, syncSelection]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.currentTarget.value
        .replace(/[^0-9]/g, "")
        .slice(0, maxLength);
      (onChange as unknown as (value: string) => void)?.(raw);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      syncSelection();
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      setSelectionStart(null);
      setSelectionEnd(null);
      onBlur?.(e);
    };

    return (
      <div
        data-slot="segmented-numeric-container"
        data-disabled={disabled || undefined}
        className={cn(disabled && "opacity-50", className)}
        onClick={() => {
          if (!disabled) localRef.current?.focus();
        }}
        style={{ cursor: disabled ? "default" : "text" }}
      >
        <div
          aria-hidden="true"
          className="flex flex-wrap pointer-events-none select-none"
        >
          {Array.from({ length: visibleSlotCount }).map((_, index) => {
            const char = rawValue[index];

            const isActive =
              isFocused &&
              selectionStart !== null &&
              selectionStart === selectionEnd &&
              (index === selectionStart ||
                (selectionStart >= visibleSlotCount &&
                  index === visibleSlotCount - 1));

            const hasFakeCaret = isActive && char === undefined;

            return (
              <div
                key={index}
                data-slot="segmented-numeric-slot"
                data-active={isActive || undefined}
                className={cn(
                  "bg-brand-neutral-700 border-input",
                  "relative flex items-center justify-center",
                  "border border-input -ml-px -mt-px",
                  "text-sm",
                  "transition-all shadow-xs outline-none",
                  index === 0 && "rounded-l-md",
                  index === visibleSlotCount - 1 && "rounded-r-md",
                  isActive && "border-ring ring-ring/50 z-10 ring-[3px]",
                  "w-8 h-8 sm:w-9 sm:h-9",
                  slotClassName
                )}
              >
                {char}
                {hasFakeCaret && (
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="animate-caret-blink bg-foreground h-4 w-px duration-1000" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <input
          ref={localRef}
          type="text"
          inputMode="numeric"
          value={rawValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onSelect={syncSelection}
          onKeyUp={syncSelection}
          disabled={disabled}
          className="sr-only"
          {...props}
        />
      </div>
    );
  }
);

SegmentedNumericInput.displayName = "SegmentedNumericInput";

export { SegmentedNumericInput };
